import React, { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Send, Square } from 'lucide-react'

import type { HgAdvisor } from '../../api/client'
import { HONEY, PersonaAvatar } from './PersonaPicker'

const BUTTON_SPRING = { type: 'spring' as const, stiffness: 400, damping: 20 }
const SURFACE_SPRING = { type: 'spring' as const, stiffness: 500, damping: 30 }
const MAX_SUGGESTIONS = 6

/** Lowercase without accents, as the server matches a mention. */
const fold = (text: string): string =>
    text.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

/** The `@word` being typed just before the caret, if there is one. */
export const mentionAt = (text: string, caret: number): { start: number; query: string } | null => {
    const match = /(^|\s)@([^\s@]{0,40})$/.exec(text.slice(0, caret))
    return match ? { start: caret - match[2].length - 1, query: match[2] } : null
}

/** Advisors whose name, or any word of it, starts with the query. */
export const mentionMatches = (advisors: HgAdvisor[], query: string): HgAdvisor[] => {
    const q = fold(query)
    return advisors
        .filter((a) => [a.name, a.first_name].some((n) => fold(n).split(/\s+/).some((w) => w.startsWith(q))))
        .slice(0, MAX_SUGGESTIONS)
}

/** One entry of the `@` list: Honey, or one of the user's advisors. */
export interface MentionOption {
    key: string
    name: string
    imageUrl: string | null
}

/** What the `@` list offers for a query: Honey first, then the advisors. */
export const mentionOptions = (advisors: HgAdvisor[], query: string): MentionOption[] => {
    const honey = fold(HONEY).startsWith(fold(query))
        ? [{ key: 'honey', name: HONEY, imageUrl: null }]
        : []
    const others = mentionMatches(advisors, query).map((a) => ({
        key: `persona:${a.persona_id}`,
        name: a.name,
        imageUrl: a.image_url,
    }))
    return [...honey, ...others].slice(0, MAX_SUGGESTIONS)
}

interface ChatComposerProps {
    /** Resolves false when the send failed; the text is then put back. */
    onSend: (text: string) => Promise<boolean>
    /** A turn is running, so Stop is offered beside Send. */
    canStop: boolean
    stopping: boolean
    onStop: () => void
    disabled?: boolean
    autoFocus?: boolean
    /** The user's advisors, offered after an `@`, after Honey. */
    advisors?: HgAdvisor[]
}

const isTouchDevice = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

const ChatComposer: React.FC<ChatComposerProps> = ({
    onSend,
    canStop,
    stopping,
    onStop,
    disabled = false,
    autoFocus = true,
    advisors = [],
}) => {
    const [text, setText] = useState('')
    const [sending, setSending] = useState(false)
    const [mention, setMention] = useState<{ start: number; query: string } | null>(null)
    const [active, setActive] = useState(0)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const suggestions = mention ? mentionOptions(advisors, mention.query) : []

    const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setText(e.target.value)
        setMention(mentionAt(e.target.value, e.target.selectionStart ?? e.target.value.length))
        setActive(0)
    }

    const choose = (option: MentionOption) => {
        const textarea = textareaRef.current
        if (!mention || !textarea) return
        const caret = textarea.selectionStart ?? text.length
        const inserted = `@${option.name} `
        const next = text.slice(0, mention.start) + inserted + text.slice(caret)
        const at = mention.start + inserted.length
        setText(next)
        setMention(null)
        requestAnimationFrame(() => {
            textarea.focus()
            textarea.setSelectionRange(at, at)
        })
    }

    useEffect(() => {
        const textarea = textareaRef.current
        if (!textarea) return
        textarea.style.height = 'auto'
        textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`
    }, [text])

    useEffect(() => {
        if (autoFocus && !disabled) textareaRef.current?.focus()
    }, [autoFocus, disabled])

    const submit = async () => {
        const body = text.trim()
        if (!body || disabled || sending) return
        setSending(true)
        setText('')
        const ok = await onSend(body)
        setSending(false)
        if (!ok) setText((current) => current || body)
        textareaRef.current?.focus()
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (suggestions.length) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault()
                const step = e.key === 'ArrowDown' ? 1 : -1
                setActive((i) => (i + step + suggestions.length) % suggestions.length)
                return
            }
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault()
                choose(suggestions[Math.min(active, suggestions.length - 1)])
                return
            }
            if (e.key === 'Escape') {
                e.preventDefault()
                setMention(null)
                return
            }
        }
        // Enter sends on a keyboard; on a phone it is a newline.
        if (e.key === 'Enter' && !e.shiftKey && !isTouchDevice()) {
            e.preventDefault()
            void submit()
        }
    }

    const canSend = text.trim().length > 0 && !disabled && !sending

    return (
        <div className="relative border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-action">
            <AnimatePresence>
                {suggestions.length > 0 && (
                    <motion.ul
                        initial={{ opacity: 0, scale: 0.95, y: 8 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 8 }}
                        transition={SURFACE_SPRING}
                        style={{ transformOrigin: 'bottom left' }}
                        className="absolute left-2 bottom-full mb-2 z-20 w-64 rounded-lg bg-white shadow-lg p-1"
                        role="listbox"
                        data-testid="chat-mention-list"
                    >
                        {suggestions.map((option, i) => (
                            <li key={option.key} role="option" aria-selected={i === active}>
                                <button
                                    type="button"
                                    // Keeps the textarea's focus and caret.
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => choose(option)}
                                    onMouseEnter={() => setActive(i)}
                                    className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm cursor-pointer ${
                                        i === active ? 'bg-blush-light' : ''
                                    }`}
                                >
                                    <PersonaAvatar name={option.name} imageUrl={option.imageUrl} size="sm" />
                                    <span className="truncate text-gray-800">{option.name}</span>
                                </button>
                            </li>
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
            <textarea
                ref={textareaRef}
                rows={1}
                value={text}
                onChange={onChange}
                onBlur={() => setMention(null)}
                onKeyDown={onKeyDown}
                placeholder="Message"
                disabled={disabled}
                aria-label="Message"
                className="w-full p-3 pr-36 text-base bg-transparent resize-none overflow-y-auto focus:outline-none"
            />
            <div className="absolute right-2 bottom-2 flex items-center gap-2">
                <AnimatePresence initial={false}>
                    {canStop && (
                        <motion.button
                            key="stop"
                            type="button"
                            onClick={onStop}
                            disabled={stopping}
                            initial={{ opacity: 0, scale: 0.6 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.6 }}
                            whileHover={stopping ? undefined : { scale: 1.1 }}
                            whileTap={stopping ? undefined : { scale: 0.9 }}
                            transition={BUTTON_SPRING}
                            className="inline-flex items-center gap-1.5 rounded-md bg-secondary hover:bg-secondary-dark px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-60 cursor-pointer disabled:cursor-default"
                        >
                            <Square className="w-3.5 h-3.5" fill="currentColor" />
                            {stopping ? 'Stopping' : 'Stop'}
                        </motion.button>
                    )}
                </AnimatePresence>
                <motion.button
                    type="button"
                    onClick={() => void submit()}
                    disabled={!canSend}
                    whileHover={canSend ? { scale: 1.1 } : undefined}
                    whileTap={canSend ? { scale: 0.9 } : undefined}
                    transition={BUTTON_SPRING}
                    aria-label="Send"
                    className="inline-flex items-center justify-center rounded-md bg-action hover:bg-action-dark p-2 text-plum disabled:opacity-40 cursor-pointer disabled:cursor-default"
                >
                    <Send className="w-4 h-4" />
                </motion.button>
            </div>
        </div>
    )
}

export default ChatComposer
